import { ColumnDef, Column } from "@tanstack/react-table";
import { DataTableColumnHeader } from "./data-table-column-header";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";
import { Button } from "@/components/ui/button";
import { formatToBRL } from "@/utils/formatToMoney";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { EmailStatusBadge, ItemStatusBadge, type AggregatedStatus, type ItemsSummary } from "./email-status-badge";
import { EmailDetailsModal } from "./email-details-modal";
import { Eye } from "lucide-react";
import { getEmailTypeLabel } from "@/pages/conciliation/stores/conciliation/email-review/types/shared";

function ActionsCell({ row }: { row: { original: any } }) {
  const { handleModal } = useDialogModal();

  const openDetails = () => {
    handleModal({
      isOpen: true,
      title: `Detalhes do E-mail #${row.original.id}`,
      description: `Loja ${row.original.store_code} - ${getEmailTypeLabel(row.original.type)}`,
      element: <EmailDetailsModal emailId={row.original.id} />,
    });
  };

  return (
    <div className="flex items-center justify-center">
      <Button variant="ghost" size="sm" onClick={openDetails}>
        <Eye className="w-4 h-4 mr-1" />
        Ver Detalhes
      </Button>
    </div>
  );
}

function DetailedItemActionsCell({ row }: { row: { original: any } }) {
  const { handleModal } = useDialogModal();

  const openDetails = () => {
    handleModal({
      isOpen: true,
      title: `Detalhes do E-mail #${row.original.email_id}`,
      description: `Loja ${row.original.code || row.original.store_code} - ${getEmailTypeLabel(row.original.type)}`,
      element: <EmailDetailsModal emailId={row.original.email_id} />,
    });
  };

  return (
    <div className="flex items-center justify-center">
      <Button variant="ghost" size="sm" onClick={openDetails}>
        <Eye className="w-4 h-4 mr-1" />
        Ver E-mail
      </Button>
    </div>
  );
}

export function getColumns(data: any[]): ColumnDef<any>[] {
  if (!data || data.length === 0) return [];
  const keys = Object.keys(data[0]);
  const columns: ColumnDef<any>[] = [];

  if (keys.includes("id")) {
    columns.push({
      accessorKey: "id",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="ID" />
      ),
    });
  }
  if (keys.includes("store_code")) {
    columns.push({
      accessorKey: "store_code",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Loja" />
      ),
    });
  }
  // Coluna de data de conciliação - após store_code (para SENT_EMAILS e DETAILED_NSU que usam store_code)
  if (keys.includes("conciliation_date") && keys.includes("store_code")) {
    columns.push({
      accessorKey: "conciliation_date",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Data Conciliação" />
      ),
      cell: ({ row }: { row: { original: any } }) => formatDateToDMY(row.original.conciliation_date),
    });
  }
  if (keys.includes("regional_name")) {
    columns.push({
      accessorKey: "regional_name",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Regional" />
      ),
      cell: ({ row }: { row: { original: any } }) =>
        row.original.regional_name || "-",
    });
  }
  if (keys.includes("responsible_name")) {
    columns.push({
      accessorKey: "responsible_name",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Responsável" />
      ),
      cell: ({ row }: { row: { original: any } }) =>
        row.original.responsible_name || "-",
    });
  }
  if (keys.includes("code")) {
    columns.push({
      accessorKey: "code",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Loja" />
      ),
    });
  }
  // Coluna de data de conciliação - após code (para DETAILED_DIVERGENCE que usa code ao invés de store_code)
  if (keys.includes("conciliation_date") && keys.includes("code") && !keys.includes("store_code")) {
    columns.push({
      accessorKey: "conciliation_date",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Data Conciliação" />
      ),
      cell: ({ row }: { row: { original: any } }) => formatDateToDMY(row.original.conciliation_date),
    });
  }
  if (keys.includes("amount")) {
    columns.push({
      accessorKey: "amount",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Valor" />
      ),
      cell: ({ row }: { row: { original: any } }) => formatToBRL(row.original?.amount),
    });
  }
  if (keys.includes("operator")) {
    columns.push({
      accessorKey: "operator",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Operador" />
      ),
    });
  }
  if (keys.includes("nsu_number")) {
    columns.push({
      accessorKey: "nsu_number",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Nsu" />
      ),
    });
  }
  if (keys.includes("regionalName")) {
    columns.push({
      accessorKey: "regionalName",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Regional" />
      ),
    });
  }
  if (keys.includes("type")) {
    columns.push({
      accessorKey: "type",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Tipo" />
      ),
      cell: ({ row }: { row: { original: any } }) =>
        getEmailTypeLabel(row.original.type),
    });
  }
  if (keys.includes("requested_by_email")) {
    columns.push({
      accessorKey: "requested_by_email",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Solicitado por" />
      ),
    });
  }
  if (keys.includes("requested_by")) {
    columns.push({
      accessorKey: "requested_by",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Solicitado por" />
      ),
    });
  }
  if (keys.includes("sent_at")) {
    columns.push({
      accessorKey: "sent_at",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Enviado em" />
      ),
      cell: ({ row }: { row: { original: any } }) => formatDateToDMY(row.original.sent_at),
    });
  }
  if (keys.includes("closed_at")) {
    columns.push({
      accessorKey: "closed_at",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Finalizado em" />
      ),
      cell: ({ row }: { row: { original: any } }) => formatDateToDMY(row.original.closed_at),
    });
  }
  if (keys.includes("aggregated_status") && keys.includes("items_summary")) {
    columns.push({
      accessorKey: "aggregated_status",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }: { row: { original: any } }) => (
        <div className="w-full flex justify-center">
          <EmailStatusBadge
            status={row.original.aggregated_status as AggregatedStatus}
            itemsSummary={row.original.items_summary as ItemsSummary}
          />
        </div>
      ),
    });
  }
  if (keys.includes("status") && !keys.includes("aggregated_status")) {
    columns.push({
      accessorKey: "status",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }: { row: { original: any } }) => (
        <div className="w-full flex justify-center">
          <ItemStatusBadge status={row.original.status} />
        </div>
      ),
    });
  }

  if (keys.includes("emailsTotal")) {
    columns.push({
      accessorKey: "emailsTotal",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Total" />
      ),
    });
  }
  if (keys.includes("emailsConferencia")) {
    columns.push({
      accessorKey: "emailsConferencia",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Conferência de Caixa" />
      ),
    });
  }
  if (keys.includes("emailsComprovante")) {
    columns.push({
      accessorKey: "emailsComprovante",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Comprovante de Nsu" />
      ),
    });
  }
  if (keys.includes("emailsLancamento")) {
    columns.push({
      accessorKey: "emailsLancamento",
      header: ({ column }: { column: Column<any, unknown> }) => (
        <DataTableColumnHeader column={column} title="Lançamento de Nsu" />
      ),
    });
  }
  if (keys.includes("conciliation_id")) {
    columns.push({
      accessorKey: "conciliation_id",
      header: () => (
        <span>Conciliação</span>
      ),
      cell: ({ row }: { row: { original: any } }) => (
        <div className="flex items-center justify-center min-h-[28px] max-w-[160px] mx-auto">
          <a
            href={`/conciliacao/lojas/conciliacao-de-caixa-de-loja/${row.original.conciliation_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline font-medium whitespace-nowrap"
          >
            Ir Para Conciliação
          </a>
        </div>
      ),
    });
  }

  // Coluna de acoes - para relatorio de emails enviados (SENT_EMAILS)
  if (keys.includes("aggregated_status") && keys.includes("id")) {
    columns.push({
      id: "actions",
      header: () => <span className="text-center w-full block">Ações</span>,
      cell: ({ row }: { row: { original: any } }) => <ActionsCell row={row} />,
    });
  }

  // Coluna de acoes - para divergencias/NSUs detalhados (tem email_id)
  if (keys.includes("email_id") && keys.includes("status") && !keys.includes("aggregated_status")) {
    columns.push({
      id: "actions",
      header: () => <span className="text-center w-full block">Ações</span>,
      cell: ({ row }: { row: { original: any } }) => (
        <DetailedItemActionsCell row={row} />
      ),
    });
  }

  return columns;
}